/**
 * Monitoring & Analytics - Enterprise Dashboard
 * Monitoring dashboard: real-time metrics + alerts (real backend data only)
 */

import { useAuthContext } from "@/contexts/AuthContext";
import { AdminLayout } from "../../layout/AdminLayout";
import { EnterprisePageHeader } from "../../components/layout/EnterprisePageHeader";
import { useMonitoring, AlertLevel } from "@/hooks/useMonitoring";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Progress } from "@/shared/components/ui/progress";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
    BarChart3,
    Cpu,
    MemoryStick,
    HardDrive,
    Network,
    AlertTriangle,
    CheckCircle,
    XCircle,
    AlertCircle,
    Globe,
    Gauge,
} from "lucide-react";

export default function MonitoringManagement() {
    const { user, logout } = useAuthContext();
    const {
        systemMetrics,
        alerts,
        alertsSummary,
        serviceHealth,
        isLoading,
        error,
        resolveAlert,
        refresh,
    } = useMonitoring();

    const handleLogout = () => {
        void logout();
    };

    const getAlertIcon = (level: AlertLevel) => {
        switch (level) {
            case "critical":
                return <XCircle className="h-4 w-4 text-red-500" />;
            case "warning":
                return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
            case "info":
                return <CheckCircle className="h-4 w-4 text-blue-500" />;
            default:
                return <AlertCircle className="h-4 w-4 text-gray-500" />;
        }
    };

    const getAlertBadge = (level: AlertLevel) => {
        switch (level) {
            case "critical":
                return <Badge variant="destructive">Critico</Badge>;
            case "warning":
                return (
                    <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                        Warning
                    </Badge>
                );
            case "info":
                return (
                    <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                        Info
                    </Badge>
                );
            default:
                return <Badge variant="outline">Unknown</Badge>;
        }
    };

    const getServiceStatusBadge = (status: string) => {
        switch (status) {
            case "healthy":
                return (
                    <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                        Healthy
                    </Badge>
                );
            case "degraded":
                return (
                    <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
                        Degraded
                    </Badge>
                );
            case "down":
                return <Badge variant="destructive">Down</Badge>;
            default:
                return <Badge variant="outline">Unknown</Badge>;
        }
    };

    const formatMB = (bytes: number) => {
        const mb = bytes / 1024 / 1024;
        return `${mb.toFixed(1)} MB`;
    };

    const formatUptimeSeconds = (seconds: number) => {
        const s = Math.max(0, Math.floor(seconds));
        const days = Math.floor(s / 86400);
        const hours = Math.floor((s % 86400) / 3600);
        const minutes = Math.floor((s % 3600) / 60);
        return `${days}d ${hours}h ${minutes}m`;
    };

    const activeAlerts = alerts.filter((alert) => !alert.resolved);
    const criticalAlerts = activeAlerts.filter(
        (alert) => alert.level === "critical",
    );

    return (
        <AdminLayout user={user} onLogout={handleLogout}>
            <div className="space-y-6 sm:space-y-8">
                <EnterprisePageHeader
                    badge="Monitoring"
                    title="System Monitoring & Analytics"
                    subtitle="Monitoraggio real-time, alerting e analisi servizi"
                    icon={<BarChart3 className="h-6 w-6" />}
                    isLoading={isLoading}
                    onRefresh={refresh}
                />

                {/* Critical Alerts Banner */}
                {criticalAlerts.length > 0 && (
                    <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-2">
                                <XCircle className="h-5 w-5" />
                                <span className="font-semibold">
                                    Alert Critici Attivi (
                                    {criticalAlerts.length})
                                </span>
                            </div>
                            <div className="space-y-1">
                                {criticalAlerts.slice(0, 3).map((alert) => (
                                    <p
                                        key={alert.id}
                                        className="text-sm text-red-600 dark:text-red-400"
                                    >
                                        • {alert.title}: {alert.message}
                                    </p>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Overview Stats */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 sm:gap-6">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Cpu className="h-4 w-4 text-blue-500" />
                                    CPU Usage
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    Live
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground">
                                {systemMetrics?.cpu_percent.toFixed(1) || "0"}%
                            </div>
                            <Progress
                                value={systemMetrics?.cpu_percent || 0}
                                className="mt-2"
                            />
                            <div className="text-sm text-muted-foreground mt-1">
                                Load:{" "}
                                {systemMetrics?.load_average?.[0]?.toFixed(2) ||
                                    "0.00"}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <MemoryStick className="h-4 w-4 text-green-500" />
                                    Memory
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    Real-time
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground">
                                {systemMetrics?.memory_percent.toFixed(1) ||
                                    "0"}
                                %
                            </div>
                            <Progress
                                value={systemMetrics?.memory_percent || 0}
                                className="mt-2"
                            />
                            <div className="text-sm text-muted-foreground mt-1">
                                Processes: {systemMetrics?.processes_count || 0}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <HardDrive className="h-4 w-4 text-orange-500" />
                                    Disk Usage
                                </div>
                                <Badge variant="outline" className="text-xs">
                                    Storage
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground">
                                {systemMetrics?.disk_percent.toFixed(1) || "0"}%
                            </div>
                            <Progress
                                value={systemMetrics?.disk_percent || 0}
                                className="mt-2"
                            />
                            <div className="text-sm text-muted-foreground mt-1">
                                Uptime:{" "}
                                {systemMetrics
                                    ? formatUptimeSeconds(
                                          systemMetrics.uptime_seconds,
                                      )
                                    : "N/A"}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm font-medium flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                    Active Alerts
                                </div>
                                <Badge
                                    variant={
                                        activeAlerts.length > 0
                                            ? "destructive"
                                            : "default"
                                    }
                                    className="text-xs"
                                >
                                    {activeAlerts.length > 0
                                        ? "Action Required"
                                        : "All Clear"}
                                </Badge>
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground">
                                {activeAlerts.length}
                            </div>
                            <div className="text-sm text-muted-foreground mt-1">
                                {criticalAlerts.length} critical,{" "}
                                {
                                    activeAlerts.filter(
                                        (a) => a.level === "warning",
                                    ).length
                                }{" "}
                                warnings
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
                    {/* System Analytics */}
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Gauge className="h-5 w-5 text-primary" />
                                System Analytics
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="overview" className="w-full">
                                <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-3">
                                    <TabsTrigger value="overview" className="text-xs sm:text-sm">
                                        Overview
                                    </TabsTrigger>
                                    <TabsTrigger value="performance" className="text-xs sm:text-sm">
                                        Performance
                                    </TabsTrigger>
                                    <TabsTrigger value="network" className="text-xs sm:text-sm">
                                        Network
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent
                                    value="overview"
                                    className="space-y-4"
                                >
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4 text-red-500" />
                                                <span className="text-sm font-medium">
                                                    Alerts (Active)
                                                </span>
                                            </div>
                                            <div className="text-2xl font-bold">
                                                {activeAlerts.length}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                {alertsSummary.critical} critical,{" "}
                                                {alertsSummary.warning} warning,{" "}
                                                {alertsSummary.info} info
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <Gauge className="h-4 w-4 text-blue-500" />
                                                <span className="text-sm font-medium">
                                                    Load (1m)
                                                </span>
                                            </div>
                                            <div className="text-2xl font-bold">
                                                {systemMetrics?.load_average?.[0]?.toFixed(2) ||
                                                    "0.00"}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                Uptime:{" "}
                                                {systemMetrics
                                                    ? formatUptimeSeconds(
                                                          systemMetrics.uptime_seconds,
                                                      )
                                                    : "N/A"}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <Network className="h-4 w-4 text-green-500" />
                                                <span className="text-sm font-medium">
                                                    Network Total
                                                </span>
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                RX:{" "}
                                                {formatMB(
                                                    systemMetrics?.network_io
                                                        ?.bytes_recv || 0,
                                                )}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                TX:{" "}
                                                {formatMB(
                                                    systemMetrics?.network_io
                                                        ?.bytes_sent || 0,
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <Cpu className="h-4 w-4 text-purple-500" />
                                                <span className="text-sm font-medium">
                                                    Processes
                                                </span>
                                            </div>
                                            <div className="text-2xl font-bold">
                                                {systemMetrics?.processes_count || 0}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                                Timestamp:{" "}
                                                {systemMetrics?.timestamp
                                                    ? new Date(
                                                          systemMetrics.timestamp,
                                                      ).toLocaleString("it-IT")
                                                    : "N/A"}
                                            </div>
                                        </div>
                                    </div>
                                </TabsContent>

                                <TabsContent
                                    value="performance"
                                    className="space-y-4"
                                >
                                    <div className="space-y-3">
                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span>CPU</span>
                                                <span>
                                                    {systemMetrics?.cpu_percent?.toFixed(
                                                        1,
                                                    ) || 0}
                                                    %
                                                </span>
                                            </div>
                                            <Progress
                                                value={
                                                    systemMetrics?.cpu_percent || 0
                                                }
                                                className="h-2"
                                            />
                                        </div>

                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span>Memory</span>
                                                <span>
                                                    {systemMetrics?.memory_percent?.toFixed(
                                                        1,
                                                    ) || 0}
                                                    %
                                                </span>
                                            </div>
                                            <Progress
                                                value={
                                                    systemMetrics?.memory_percent || 0
                                                }
                                                className="h-2"
                                            />
                                        </div>

                                        <div>
                                            <div className="flex justify-between text-sm mb-1">
                                                <span>Disk Usage</span>
                                                <span>
                                                    {systemMetrics?.disk_percent?.toFixed(
                                                        1,
                                                    ) || 0}
                                                    %
                                                </span>
                                            </div>
                                            <Progress
                                                value={
                                                    systemMetrics?.disk_percent || 0
                                                }
                                                className="h-2"
                                            />
                                        </div>
                                    </div>

                                    <div className="pt-4 border-t">
                                        <h4 className="font-semibold mb-2">
                                            System Info
                                        </h4>
                                        <div className="text-sm space-y-1 text-muted-foreground">
                                            <p>
                                                Uptime:{" "}
                                                {systemMetrics
                                                    ? formatUptimeSeconds(
                                                          systemMetrics.uptime_seconds,
                                                      )
                                                    : "N/A"}
                                            </p>
                                            <p>
                                                Processes:{" "}
                                                {systemMetrics?.processes_count ||
                                                    "N/A"}
                                            </p>
                                            <p>
                                                Load Average:{" "}
                                                {systemMetrics?.load_average?.join(
                                                    ", ",
                                                ) || "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                </TabsContent>

                                <TabsContent
                                    value="network"
                                    className="space-y-4"
                                >
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                                            <div className="flex items-center gap-2">
                                                <Network className="h-4 w-4 text-blue-500" />
                                                <span className="font-medium">
                                                    RX Total
                                                </span>
                                            </div>
                                            <span className="font-mono">
                                                {formatMB(
                                                    systemMetrics?.network_io
                                                        ?.bytes_recv || 0,
                                                )}
                                            </span>
                                        </div>

                                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                                            <div className="flex items-center gap-2">
                                                <Network className="h-4 w-4 text-green-500" />
                                                <span className="font-medium">
                                                    TX Total
                                                </span>
                                            </div>
                                            <span className="font-mono">
                                                {formatMB(
                                                    systemMetrics?.network_io
                                                        ?.bytes_sent || 0,
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    {/* Alerts & Services */}
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-card/50">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-primary" />
                                Alerts & Services
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="alerts" className="w-full">
                                <TabsList className="grid h-auto w-full grid-cols-2 gap-1">
                                    <TabsTrigger value="alerts" className="text-xs sm:text-sm">
                                        Alerts ({activeAlerts.length})
                                    </TabsTrigger>
                                    <TabsTrigger value="services" className="text-xs sm:text-sm">
                                        Services
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent
                                    value="alerts"
                                    className="space-y-4"
                                >
                                    <ScrollArea className="h-80">
                                        {alerts.length > 0 ? (
                                            <div className="space-y-3">
                                                {alerts
                                                    .slice(0, 10)
                                                    .map((alert) => (
                                                        <div
                                                            key={alert.id}
                                                            className="p-3 border rounded-lg"
                                                        >
                                                            <div className="flex items-center justify-between mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    {getAlertIcon(
                                                                        alert.level,
                                                                    )}
                                                                    <span className="font-medium text-sm">
                                                                        {
                                                                            alert.title
                                                                        }
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {getAlertBadge(
                                                                        alert.level,
                                                                    )}
                                                                    {!alert.resolved && (
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline"
                                                                            onClick={() =>
                                                                                resolveAlert(
                                                                                    alert.id,
                                                                                )
                                                                            }
                                                                        >
                                                                            <CheckCircle className="h-3 w-3" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground mb-2">
                                                                {alert.message}
                                                            </p>
                                                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                                <span>{alert.type}</span>
                                                                <span>
                                                                    {new Date(
                                                                        alert.timestamp,
                                                                    ).toLocaleString(
                                                                        "it-IT",
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted-foreground py-8">
                                                <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                <p>Nessun alert attivo</p>
                                            </div>
                                        )}
                                    </ScrollArea>
                                </TabsContent>

                                <TabsContent
                                    value="services"
                                    className="space-y-4"
                                >
                                    <ScrollArea className="h-80">
                                        <div className="space-y-3">
                                            {serviceHealth.map(
                                                (service, index) => (
                                                    <div
                                                        key={index}
                                                        className="p-3 border rounded-lg"
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <Globe className="h-4 w-4" />
                                                                <span className="font-medium">
                                                                    {
                                                                        service.service
                                                                    }
                                                                </span>
                                                            </div>
                                                            {getServiceStatusBadge(
                                                                service.status,
                                                            )}
                                                        </div>
                                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 text-sm">
                                                            <div>
                                                                <span className="text-muted-foreground">
                                                                    Response
                                                                    Time:
                                                                </span>
                                                                <span className="ml-1 font-mono">
                                                                    {
                                                                        service.response_time_ms
                                                                    }
                                                                    ms
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <span className="text-muted-foreground">
                                                                    Last Check:
                                                                </span>
                                                                <span className="ml-1 font-mono">
                                                                    {
                                                                        new Date(service.last_check).toLocaleTimeString("it-IT")
                                                                    }
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {service.details && (
                                                            <p className="text-xs text-muted-foreground mt-2">
                                                                {
                                                                    service.details
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </ScrollArea>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                </div>

                {/* Error Display */}
                {error && (
                    <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                <AlertCircle className="h-4 w-4" />
                                <span className="font-medium">
                                    Errore di connessione
                                </span>
                            </div>
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                {error}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </AdminLayout>
    );
}
